import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Dealer } from "src/app/shared/models/Dealer.model";
import { DealerBillingCount, DealerBillingDetail } from "src/app/shared/models/DTO/dealerBillingDetail.dto";
import { AppMessageSeverity } from "src/app/shared/models/enums/AppMessageSeverity.enum";
import { FocusPayDevice } from "src/app/shared/models/FocusPay/FocusPayDevice.model";
import { License } from "src/app/shared/models/license.model";
import { Store } from "src/app/shared/models/store";
import { UserVenue } from "src/app/shared/models/user.model";
import { VenueStatus } from "src/app/shared/models/VenueStatus.model";
import { AppService } from "./app.service";
import { FocuslinkService } from "./focuslink.service";

@Injectable({
	providedIn: "root",
})
export class DealerService {
	dealer: BehaviorSubject<Dealer> = new BehaviorSubject<Dealer>(new Dealer());
	dealerVenues: BehaviorSubject<Store[]> = new BehaviorSubject<Store[]>([]);
	dealerLicenses: BehaviorSubject<License[]> = new BehaviorSubject<License[]>([]);
	dealerBillingDetail: BehaviorSubject<DealerBillingDetail> = new BehaviorSubject<DealerBillingDetail>(new DealerBillingDetail());
	dealerBillingCount: BehaviorSubject<DealerBillingCount> = new BehaviorSubject<DealerBillingCount>(new DealerBillingCount());

	dealerList: BehaviorSubject<Dealer[]> = new BehaviorSubject<Dealer[]>([]);

	dealerFocusPayDeviceList: BehaviorSubject<FocusPayDevice[]> = new BehaviorSubject<FocusPayDevice[]>([]);

	userVenues: UserVenue[];

	constructor(public app: AppService, public focuslink: FocuslinkService) {}

	initDealer(dealerKey: string, userVenues?: UserVenue[]) {
		this.app.loading(true);
		this.userVenues = userVenues;
		this.dealerGet(dealerKey);
		this.focusPayDevicesDealer(dealerKey);

		setInterval(() => {
			this.venueStatusRefresh();
		}, 180000);
	}

	initSuperUser() {
		this.focusPayDevicesDealer();
		this.dealerListGet();
	}

	async dealerGet(dealerKey: string) {
		const resp = await this.focuslink.dealerGetOne(dealerKey, false, false, true, this.userVenuesBuild());
		const dealer = new Dealer(resp);
		dealer.venues.sort((a, b) => (a.name > b.name ? 1 : -1));
		dealer.venues.forEach((v) => {
			v.status?.stationStatus?.sort((a, b) => (a.deviceId > b.deviceId ? 1 : -1));
		});
		this.dealerVenues.next(dealer.venues);
		this.dealerLicenses.next(dealer.licenses);
		if (dealer.billing) {
			this.dealerBillingDetail.next(dealer.billing);
			this.dealerBillingCount.next(dealer.counts);
		}

		this.dealer.next(dealer);
		this.app.devLog(this.dealer.value);
		this.app.loading(false);
		return dealer;
	}

	async dealerListGet() {
		const resp = await this.focuslink.dealersGet(false);
		this.dealerList.next(resp.map((i) => new Dealer(i)));
		return this.dealerList.value;
	}

	async focusPayDevicesDealer(dealerKey?: string) {
		const resp = await this.focuslink.focusPayDeviceGet(undefined, dealerKey);
		this.dealerFocusPayDeviceList.next(
			resp.map((i) => {
				const d = new FocusPayDevice(i);
				d.deviceId = `${d.type}-${d.key}`;
				return d;
			})
		);
		return this.dealerFocusPayDeviceList.value;
	}

	async saveDealer(dealer?: Dealer) {
		const resp = await this.focuslink.dealerPost(dealer || this.dealer.value);
		const savedDealer = new Dealer(resp);
		if (this.dealer.value.key === savedDealer.key) {
			Object.assign(this.dealer.value, savedDealer);
			this.dealer.next(this.dealer.value);
		}
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Dealer settings saved successfully.` });
		return savedDealer;
	}

	async venueStatusRefresh() {
		const b64VenueKeylist = btoa(this.dealer.value.venues.map((i) => i.key.toString()).toString());
		const resp = await this.focuslink.dealerVenueStatus(this.dealer.value.key, b64VenueKeylist);
		const venueStatus: VenueStatus[] = resp.map((i) => new VenueStatus(i));
		this.dealer.value.venues.forEach((v) => {
			v.status = venueStatus.find((i) => i.pk === v.key.toString());
			v.status?.stationStatus?.sort((a, b) => (a.deviceId > b.deviceId ? 1 : -1));
		});
		this.dealer.next(this.dealer.value);
		this.dealerVenues.value.forEach((v) => {
			v.status = venueStatus.find((i) => i.pk === v.key.toString());
		});
		this.dealerVenues.next(this.dealerVenues.value);

		return this.dealer.value;
	}

	userVenuesBuild() {
		let uv;
		if (this.userVenues && this.userVenues.length > 0) {
			uv = btoa(this.userVenues.map((i) => i.venueKey).toString());
		}
		return uv;
	}
}
